import { Inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { GET_SAMPLE_AI_ANALYSIS_PROGRESS_QUERY } from '../../../tokens/use-case.tokens';
import {
  type GetSampleAiAnalysisProgressOutput,
  // Concrete class (not the IGetSampleAiAnalysisProgressUseCase interface): the
  // @Inject() parameter type must be a runtime value, because jest's
  // downleveled ctorParameters references the annotation's identifier.
  GetSampleAiAnalysisProgressUseCase,
  type SampleAiAnalysisStatus,
} from '@analysis/use-cases';
import { EMPTY, Observable, Subject, from, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { DEFAULT_SAMPLE_AI_PROGRESS_POLL_CONFIG } from './sample-ai-analysis-polling';

export interface SampleAiAnalysisProgressLoadParams {
  sampleId: string;
  methodTypeId: string;
  poll: boolean;
}

interface SampleAiAnalysisProgressState {
  status?: SampleAiAnalysisStatus;
  sampleId?: string;
  pollingExhausted?: boolean;
}

const initialState: SampleAiAnalysisProgressState = {};

@Injectable()
export class SampleAiAnalysisProgressStore extends ComponentStore<SampleAiAnalysisProgressState> {
  private readonly dataRefreshTickSubject = new Subject<SampleAiAnalysisStatus | undefined>();

  public readonly dataRefreshTick$ = this.dataRefreshTickSubject.asObservable();

  public readonly status$ = this.select((state) => state.status);
  public readonly inProgress$ = this.select((state) => state.status === 'inProgress');
  public readonly error$ = this.select((state) => state.status === 'error');
  public readonly pollingExhausted$ = this.select((state) => state.pollingExhausted ?? false);

  public readonly aiAnalysisCompleted$ = this.status$.pipe(
    pairwise(),
    filter(([previous, current]) => previous === 'inProgress' && current === 'ready'),
    map(() => undefined as void)
  );

  constructor(
    @Inject(GET_SAMPLE_AI_ANALYSIS_PROGRESS_QUERY)
    private readonly getSampleAiAnalysisProgressUseCase: GetSampleAiAnalysisProgressUseCase
  ) {
    super(initialState);
  }

  readonly loadProgress = this.effect((params$: Observable<SampleAiAnalysisProgressLoadParams | null>) =>
    params$.pipe(
      distinctUntilChanged(
        (a, b) => a?.sampleId === b?.sampleId && a?.methodTypeId === b?.methodTypeId && a?.poll === b?.poll
      ),
      switchMap((params) => {
        // A null payload means there is nothing to resolve (e.g. sample or
        // method type missing). Cancel any in-flight request and clear status.
        if (!params) {
          this.patchState({ status: undefined, sampleId: undefined, pollingExhausted: false });
          return EMPTY;
        }
        if (this.get().sampleId !== params.sampleId) {
          this.patchState({ status: undefined, sampleId: params.sampleId, pollingExhausted: false });
        }
        return params.poll
          ? this.pollProgress(params.sampleId, params.methodTypeId)
          : this.fetchProgressOnce(params.sampleId, params.methodTypeId);
      })
    )
  );

  private fetchProgressOnce(sampleId: string, methodTypeId: string): Observable<GetSampleAiAnalysisProgressOutput> {
    return from(this.getSampleAiAnalysisProgressUseCase.execute({ sampleId, methodTypeId })).pipe(
      tap((output) => this.patchState({ status: output.status })),
      catchError(() => {
        this.patchState({ status: undefined });
        return EMPTY;
      })
    );
  }

  private pollProgress(sampleId: string, methodTypeId: string): Observable<GetSampleAiAnalysisProgressOutput> {
    const { intervalMs, maxAttempts, dataRefreshEveryNPolls } = DEFAULT_SAMPLE_AI_PROGRESS_POLL_CONFIG;

    return timer(0, intervalMs).pipe(
      take(maxAttempts),
      concatMap((attemptIndex) =>
        from(this.getSampleAiAnalysisProgressUseCase.execute({ sampleId, methodTypeId })).pipe(
          tap((output) => {
            this.patchState({ status: output.status });
            const periodic = (attemptIndex + 1) % dataRefreshEveryNPolls === 0;
            if (periodic || output.status === 'ready') {
              this.dataRefreshTickSubject.next(output.status);
            }
          }),
          map((output) => ({ status: output.status, attemptIndex })),
          catchError(() => {
            this.patchState({ status: undefined });
            return EMPTY;
          })
        )
      ),
      tap(({ status, attemptIndex }) => {
        if (status === 'inProgress' && attemptIndex === maxAttempts - 1) {
          this.patchState({ pollingExhausted: true });
        }
      }),
      takeWhile(({ status, attemptIndex }) => status === 'inProgress' && attemptIndex < maxAttempts - 1),
      map(({ status }) => ({ status }))
    );
  }
}
